import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AdminShell from "../AdminShell";
import "./admin-login.css";

/**
 * RAdminLogin — /admin/login
 *
 * Redesigned admin sign-in (teal admin identity), visuals per
 * design-ref/admin/Login.html; functionality replicated 1:1 from the legacy
 * frontend/src/pages/AdminLoginPage.js:
 *   - POST /api/admin/login {email, password}
 *   - Pydantic array-detail + string-detail error handling
 *   - stores adminToken + adminTokenExpires, removes managerMode
 *   - permission-based redirect (full access -> /admin/dashboard,
 *     bookings.read-only -> /admin/live-dashboard)
 *   - show/hide password toggle, disabled "Signing in…" busy state,
 *     red error alert above the submit button
 *
 * Login screens render standalone (no sidebar/header), so the page uses
 * AdminShell's chromeless mode purely for the `.uma` token wrapper + toast
 * provider — there is no auth guard here (this IS the login screen).
 */
const RAdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(errorData.detail)) {
          const errorMsg = errorData.detail
            .map((err) => err.msg || err.message)
            .join(", ");
          throw new Error(errorMsg || "Validation failed");
        }
        throw new Error(errorData.detail || "Login failed");
      }

      const data = await response.json();

      // Store token in localStorage
      localStorage.setItem("adminToken", data.access_token);
      localStorage.setItem("adminTokenExpires", Date.now() + data.expires_in * 1000);
      localStorage.removeItem("managerMode"); // full admin login is not manager-restricted

      // Check permissions and redirect accordingly
      const permissions = data.permissions || [];
      const hasFullAccess = permissions.some(
        (p) =>
          p.includes("admins.") ||
          p.includes("products.") ||
          p.includes("orders.") ||
          p.includes("analytics.")
      );

      if (!hasFullAccess && permissions.includes("bookings.read")) {
        // Live Dashboard only user - redirect directly to live dashboard
        navigate("/admin/live-dashboard");
      } else {
        // Full access user - redirect to main dashboard
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell chromeless pageClass="pg-admin-login">
      <div className="pg-admin-login">
        <div className="al-box">
          <div className="al-brand">
            <span className="al-brand-tile">
              <img src="/design/assets/umoona-emblem.png" alt="Umoona Opal Mine & Museum" />
            </span>
            <div className="al-brand-title">Admin Portal</div>
            <div className="al-brand-eyebrow">UMOONA OPAL MINE &amp; MUSEUM</div>
          </div>

          <div className="al-card">
            <div className="al-card-head">
              <div className="al-card-title">Sign in to your account</div>
              <span className="al-badge">Administrator Access</span>
            </div>

            <form onSubmit={handleLogin}>
              <label className="al-field">
                <span className="al-label">Email</span>
                <input
                  className="al-input"
                  type="email"
                  placeholder="admin@umoonaopalmine.com.au"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </label>

              <label className="al-field al-field--pass">
                <span className="al-label">Password</span>
                <div className="al-passwrap">
                  <input
                    className="al-input al-input--pass"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="al-eye"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              {error && (
                <div className="al-error" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="al-submit" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="al-help">Need help? Contact your system administrator</div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default RAdminLogin;
